export interface UCConfigInterface {
	configId: string;
	isTCFEnabled: boolean;
}

export interface ServicesConsents {
	id: string;
	consent: boolean;
}

export type ConsentActionType =
	| 'onAcceptAllServices'
	| 'onDenyAllServices'
	| 'onEssentialChange'
	| 'onInitialPageLoad'
	| 'onNonEURegion'
	| 'onSessionRestored'
	| 'onTcfStringChange'
	| 'onUpdateServices'
	| 'onMobileSessionRestore';

export type SettingType = 'TCF' | 'GDPR' | 'CCPA';

export interface SettingData {
	id: string;
	type: SettingType;
	version: string;
	abVariant?: string;
	sandbox?: true;
}

export type ConsentType = 'IMPLICIT' | 'EXPLICIT';

export interface ConsentData {
	status: 'ALL_ACCEPTED' | 'ALL_DENIED' | 'SOME_ACCEPTED' | 'SOME_DENIED';
	serviceIds?: string[];
	required: boolean;
	version: number;
	controllerId: string;
	language: string;
	createdAt: number;
	updatedAt: number;
	updatedBy: ConsentActionType;
	setting: SettingData;
	type: ConsentType;
	hash: string;
	gpcSignal?: boolean;
	isBot?: true;
	isOutsideEu?: true;
}

export interface ServiceData {
	name: string;
	version: string;
	category: string;
	essential: boolean;
	consent?: {
		given: boolean;
		type: ConsentType;
	};
	gcm?: {
		analyticsStorage?: true;
		adStorage?: true;
	};
	subservices?: Record<string, ServiceData>;
	thirdCountryDataTransfer?: boolean;
	status?: 'added';
}

export interface CategoryData {
	essential?: boolean;
	state: 'ALL_DENIED' | 'SOME_ACCEPTED' | 'ALL_ACCEPTED';
	dps: Record<string, boolean> | null;
	hidden?: boolean;
}

export interface ConsentDetails {
	consent: ConsentData;
	services: Record<string, ServiceData>;
	categories: Record<string, CategoryData>;
}

export interface DataProcessingServiceConsent {
	name: string;
	id: string;
	category: string;
	status: boolean;
	hasAnswered: boolean;
}

export enum DataProcessingService {
	DOUBLECLICK_AD = 'DOUBLECLICK_AD',
	DOUBLECLICK_FLOODLIGHT = 'DOUBLECLICK_FLOODLIGHT',
	FACEBOOK_PIXEL = 'FACEBOOK_PIXEL',
	FACEBOOK_SOCIAL_PLUGINS = 'FACEBOOK_SOCIAL_PLUGINS',
	FIREBASE_AUTH = 'FIREBASE_AUTH',
	GOOGLE_ADS = 'GOOGLE_ADS',
	GOOGLE_ADSENSE = 'GOOGLE_ADSENSE',
	GOOGLE_ANALYTICS = 'GOOGLE_ANALYTICS',
	GOOGLE_FONTS = 'GOOGLE_FONTS',
	GOOGLE_TAG_MANAGER = 'GOOGLE_TAG_MANAGER',
	GSTATIC = 'GSTATIC',
	JUSTWATCH = 'JUSTWATCH',
	MARFEEL = 'MARFEEL',
	PINTEREST = 'PINTEREST',
	RECAPTCHA = 'RECAPTCHA',
	SENTRY = 'SENTRY',
	SNAPCHAT_PIXEL = 'SNAPCHAT_PIXEL',
	SPOTX = 'SPOTX',
	TIKTOK = 'TIKTOK',
	TWITTER_ANALYTICS = 'TWITTER_ANALYTICS',
	USERCENTRICS_CMP = 'USERCENTRICS_CMP',
	YOUTUBE_VIDEOS = 'YOUTUBE_VIDEOS',
	ZENDESK = 'ZENDESK',
}

export type UC_UI_EVENT_DETAIL =
	| { type: 'CMP_SHOWN'; source: 'FIRST_LAYER' }
	| { type: 'MORE_INFORMATION_LINK'; source: 'FIRST_LAYER' }
	| { type: 'ACCEPT_ALL'; source: 'FIRST_LAYER' | 'SECOND_LAYER' | 'UC_UI_API' }
	| { type: 'SAVE'; source: 'FIRST_LAYER' | 'SECOND_LAYER' | 'UC_UI_API' }
	| { type: 'CCPA_TOGGLES_ON'; source: 'FIRST_LAYER' | 'SECOND_LAYER' }
	| { type: 'CCPA_TOGGLES_OFF'; source: 'FIRST_LAYER' | 'SECOND_LAYER' }
	| { type: 'DENY_ALL'; source: 'none' };

declare global {
	interface Window {
		__ucCmp: {
			showFirstLayer: () => void;
			showSecondLayer: () => void;
			acceptAllConsents: () => Promise<void> | null;
			clearUserSession: () => Promise<void>;
			getConsentDetails: () => Promise<ConsentDetails>;
		};
		UC_UI_SUPPRESS_CMP_DISPLAY: boolean;
	}
}
